/**
 * tenantDateTime — anchors the app's datetime runtime to the TENANT's own
 * configured timezone at boot.
 *
 * Why this exists (the defect it closes): every timestamp the API serves is a
 * correct offset-bearing ISO-8601 instant (`...Z`). Rendering it for a human
 * therefore requires a display zone, and `@v0y4ge/jrni-*`'s datetime runtime
 * (cd-app/lib/datetime.ts) defaults to the BROWSER's local zone when nothing
 * configures it. Until this ran, the only caller of `configureDateTime` was the
 * Settings -> General screen — a user-driven surface most sessions never open —
 * so the whole app formatted tenant data in whatever zone the viewer's laptop
 * happened to sit in. On an operator machine in the tenant's own zone that
 * looks correct and is correct by accident; for every other viewer it silently
 * renders the wrong wall-clock time.
 *
 * The zone comes from `getTenantPublicConfig().timezone`, resolved server-side
 * from tenant config. It is NEVER defaulted here: no fallback zone is applied,
 * because a wrong-but-plausible time is worse than an unanchored one, and the
 * platform frontend carries no tenant identity of its own. A tenant with no
 * configured zone fails loud at the route.
 */
import { useEffect } from "react";
import { getTenantPublicConfig, type StudioSettingsRequestOptions } from "@v0y4ge/api-client";
import { configureDateTime } from "../cd-app/lib/datetime";

const OPTS: StudioSettingsRequestOptions = {
  baseUrl: ((import.meta.env.VITE_SETTINGS_API_URL as string | undefined) ?? "/api"),
  surface: "settings",
};

let applied = false;
let inFlight = false;

/** Fetch the tenant timezone once and configure the datetime runtime with it. */
export function applyTenantTimeZone(): void {
  if (applied || inFlight || typeof getTenantPublicConfig !== "function") return;
  inFlight = true;
  (getTenantPublicConfig as (o: StudioSettingsRequestOptions) => Promise<unknown>)(OPTS)
    .then((res: unknown) => {
      const raw = res as Record<string, unknown> | null;
      const body = (raw && typeof raw === "object" && "data" in raw ? raw.data : raw) as
        | { timezone?: unknown }
        | null;
      const tz = typeof body?.timezone === "string" ? body.timezone.trim() : "";
      if (!tz) {
        console.warn("tenant public config carries no timezone; dates will render in the viewer local zone");
        return;
      }
      configureDateTime({ timeZone: tz });
      applied = true;
    })
    .catch((err: unknown) => {
      console.warn("tenant timezone lookup failed; dates will render in the viewer local zone:", err);
    })
    .finally(() => {
      inFlight = false;
    });
}

/** React hook: applies the tenant timezone as soon as `ready` goes true. */
export function useTenantTimeZone(ready: boolean): void {
  useEffect(() => {
    if (!ready) return;
    applyTenantTimeZone();
  }, [ready]);
}

/** Test seam: forget the applied/in-flight state so a suite can re-drive boot. */
export function __resetTenantTimeZoneForTests(): void {
  applied = false;
  inFlight = false;
}
